import { array, boolean, date, nativeEnum, object, string, union, ZodIssueCode } from 'zod';

import { IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { Weekday } from '@occc/graphql';

const EITHER_DAYS_OF_WEEK_OR_TIME_OF_DAY_ERROR_MESSAGE_TEXT =
  'Days of Week or Time of Day must be configured.';

const TimeOfDaySchema = object({
  id: string().optional(),
  availableDailyStartingAtTime: union([string(), date()]).optional(),
  availableDailyEndingAtTime: union([string(), date()]).optional(),
}).optional();

const DaysOfWeekSchema = object({
  id: string().optional(),
  availableWeeklyOnDays: array(nativeEnum(Weekday)).optional(),
}).optional();

export const FulfillmentWindowSchema = object({
  isEnabled: boolean({ required_error: IS_REQUIRED }),
  timezone: string().optional(),
  timeOfDay: TimeOfDaySchema,
  daysOfWeek: DaysOfWeekSchema,
}).superRefine((data, ctx) => {
  if (!data.isEnabled) {
    return;
  }

  if (!data.timezone) {
    ctx.addIssue({ code: ZodIssueCode.custom, path: ['timezone'], message: IS_REQUIRED });
  }

  const { availableDailyStartingAtTime, availableDailyEndingAtTime } = data.timeOfDay ?? {};

  if (availableDailyStartingAtTime && !availableDailyEndingAtTime) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: IS_REQUIRED,
      path: ['timeOfDay', 'availableDailyEndingAtTime'],
    });
  }

  if (!availableDailyStartingAtTime && availableDailyEndingAtTime) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: IS_REQUIRED,
      path: ['timeOfDay', 'availableDailyStartingAtTime'],
    });
  }

  if (
    availableDailyStartingAtTime &&
    availableDailyEndingAtTime &&
    availableDailyEndingAtTime <= availableDailyStartingAtTime
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: 'Start time must be before End time',
      path: ['timeOfDay', 'availableDailyStartingAtTime'],
    });
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: 'End time must be after Start time',
      path: ['timeOfDay', 'availableDailyEndingAtTime'],
    });
  }

  const hasDaysOfWeek = !!data.daysOfWeek?.availableWeeklyOnDays?.length;
  const hasTimeOfDay = !!availableDailyStartingAtTime || !!availableDailyEndingAtTime;

  if (!hasDaysOfWeek && !hasTimeOfDay) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: EITHER_DAYS_OF_WEEK_OR_TIME_OF_DAY_ERROR_MESSAGE_TEXT,
      path: ['daysOfWeek', 'availableWeeklyOnDays'],
    });
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: EITHER_DAYS_OF_WEEK_OR_TIME_OF_DAY_ERROR_MESSAGE_TEXT,
      path: ['timeOfDay', 'availableDailyStartingAtTime'],
    });
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: EITHER_DAYS_OF_WEEK_OR_TIME_OF_DAY_ERROR_MESSAGE_TEXT,
      path: ['timeOfDay', 'availableDailyEndingAtTime'],
    });
  }
});
